// Bildirim Merkezi — macOS Bildirim Merkezi düzeni: sağda kalıcı panel (varsayılan AÇIK, zilden aç/kapat),
// bildirimler UYGULAMAYA (modüle) göre gruplanır ve yığın kart olarak durur; yığına tıklayınca açılır.
// "ÖNEMLİ" etiketi sunucunun `important` bayrağından (inbox_service.PRIMARY_TYPES — eylem bekleyen / bana yönelen).
// Panel yalnız OKUNMAMIŞ bildirimleri gösterir: kartı açmak ya da ✕ ile temizlemek = okundu (Bildirimler sayfasında
// kalır, silinmez). Panel görünmüyorken gelen bildirim sağ üstte macOS banner'ı olarak çıkar.
// Bildirimler sayfası (/notifications) aynen durur — tam geçmiş orada.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import {
  X, Bell, BellOff, CheckCheck, ExternalLink, ChevronUp, FileText, ShoppingCart, Users, Wallet, MessageSquare,
  MessagesSquare, CheckSquare, Handshake, Factory, Package, RotateCcw, ShieldCheck, CalendarClock, Workflow,
  Megaphone, Mail, Fingerprint, Receipt, Plane, HandCoins, Bot, Database, Building2,
} from 'lucide-react'
import api from '../../services/api'
import { useAppStore } from '../../stores/appStore'
import { useChatDock } from '../../stores/chatDockStore'
import { openNotification } from '../../utils/notificationLink'
import { companyBadge } from '../../utils/formatters'

// ── panel durumu ───────────────────────────────────────────────────────────────
const OPEN_KEY = 'vertex.notifPanel.open'
const readOpen = () => { try { return localStorage.getItem(OPEN_KEY) !== '0' } catch { return true } }
const WIDE = '(min-width: 1280px)'
const isWide = () => typeof window !== 'undefined' && (window.matchMedia?.(WIDE)?.matches ?? true)

export const useNotifPanel = create((set, get) => ({
  open: readOpen(),          // geniş ekranda yerleşik panel (kalıcı tercih, varsayılan açık)
  overlay: false,            // dar ekranda üste açılan panel (oturumluk)
  toggle: () => {
    if (isWide()) {
      const v = !get().open
      try { localStorage.setItem(OPEN_KEY, v ? '1' : '0') } catch { /* yut */ }
      set({ open: v })
    } else set({ overlay: !get().overlay })
  },
  close: () => {
    if (isWide()) { try { localStorage.setItem(OPEN_KEY, '0') } catch { /* yut */ } set({ open: false }) } else set({ overlay: false })
  },
}))

function useWide() {
  const [w, setW] = useState(isWide)
  useEffect(() => {
    const mq = window.matchMedia?.(WIDE)
    if (!mq) return undefined
    const h = () => setW(mq.matches)
    mq.addEventListener?.('change', h)
    return () => mq.removeEventListener?.('change', h)
  }, [])
  return w
}

/** Panel şu an ekranda mı? (banner bu durumda çıkmaz — kart panele düşer.) */
export function useNotifPanelVisible() {
  const wide = useWide()
  const open = useNotifPanel((s) => s.open)
  const overlay = useNotifPanel((s) => s.overlay)
  return wide ? open : overlay
}

// ── veri ───────────────────────────────────────────────────────────────────────
export function useNotifFeed() {
  return useQuery({
    queryKey: ['notifications-panel'],
    queryFn: async () => {
      const r = await api.get('/notifications/', { params: { limit: 100, unread_only: true }, timeout: 10000, silentNetToast: true })
      return r.data?.data || { items: [], unread_count: 0 }
    },
    refetchInterval: 60_000,
  })
}

// ── uygulama (modül) sözlüğü: bildirim tipinin öneki → ad, simge, renk ─────────
const APPS = [
  [/^(approval_|quote\.|quote_|order\.|delivery\.|invoice\.)/, 'Satış', FileText, 'bg-violet-500'],
  [/^purchase\./, 'Satınalma', ShoppingCart, 'bg-sky-500'],
  [/^expense\./, 'Masraf', Receipt, 'bg-emerald-500'],
  [/^advance\./, 'Avans', HandCoins, 'bg-emerald-600'],
  [/^hcm_leave\./, 'İzin', Plane, 'bg-teal-500'],
  [/^(duty\.|pdks\.|timesheet\.)/, 'İnsan Kaynakları', Users, 'bg-teal-600'],
  [/^(pay\.|payreq\.|collection\.|cfo\.)/, 'Finans', Wallet, 'bg-amber-500'],
  [/^chat\./, 'Sohbet', MessageSquare, 'bg-blue-500'],
  [/^doc_|^mention$/, 'Belge görüşmesi', MessagesSquare, 'bg-blue-600'],
  [/^whatsapp\./, 'WhatsApp', MessagesSquare, 'bg-green-500'],
  [/^(mail_|mail\.)/, 'Posta', Mail, 'bg-indigo-500'],
  [/^(task_|task\.|job\.)/, 'Görevler', CheckSquare, 'bg-pink-500'],
  [/^(crm\.|web\.)/, 'CRM', Handshake, 'bg-orange-500'],
  [/^(bom\.|mfg\.)/, 'İmalat', Factory, 'bg-stone-500'],
  [/^(product\.|siemens\.)/, 'Ürünler', Package, 'bg-cyan-600'],
  [/^return\./, 'İade', RotateCcw, 'bg-rose-500'],
  [/^audit\./, 'İç Denetim', ShieldCheck, 'bg-slate-600'],
  [/^mtg\./, 'Toplantılar', CalendarClock, 'bg-purple-500'],
  [/^(wf\.|seq\.)/, 'Otomasyon', Workflow, 'bg-fuchsia-500'],
  [/^(mkt\.|linkedin\.|instagram\.)/, 'Pazarlama', Megaphone, 'bg-red-500'],
  [/^(membership\.|partner\.)/, 'Üyelik ve iş ortakları', Building2, 'bg-gray-500'],
  [/^(ai\.|daily_report\.)/, 'Asistan ve raporlar', Bot, 'bg-violet-600'],
  [/^(backup\.|etl\.)/, 'Sistem', Database, 'bg-gray-600'],
]
export function appOf(type) {
  const t = String(type || '')
  for (const [re, label, icon, color] of APPS) if (re.test(t)) return { key: label, label, icon, color }
  return { key: 'VERTEX', label: 'VERTEX', icon: Fingerprint, color: 'bg-gray-700' }
}

// Sunucu SYSUTCDATETIME → saat dilimi yoksa UTC kabul edilir.
function parseUtc(iso) {
  if (!iso) return null
  const d = new Date(/Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}
export function agoText(iso) {
  const d = parseUtc(iso)
  if (!d) return ''
  const now = new Date()
  const sec = Math.floor((now - d) / 1000)
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (sec < 60) return 'şimdi'
  if (sec < 3600) return `${Math.floor(sec / 60)} dk önce`
  if (d.toDateString() === now.toDateString()) return hhmm
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return `Dün ${hhmm}`
  if (sec < 6 * 86400) return `${['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'][d.getDay()]} ${hhmm}`
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${hhmm}`
}

// ── okundu / temizle ───────────────────────────────────────────────────────────
function useClear() {
  const qc = useQueryClient()
  const drop = (ids) => {
    const set = new Set(ids.map(String))
    qc.setQueryData(['notifications-panel'], (old) => {
      if (!old) return old
      const items = (old.items || []).filter((n) => !set.has(String(n.id)))
      return { ...old, items, unread_count: Math.max(0, (old.unread_count || 0) - (old.items.length - items.length)) }
    })
  }
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['notifications-panel'] })
    qc.invalidateQueries({ queryKey: ['notifications-feed'] })
    qc.invalidateQueries({ queryKey: ['notifications-unread-count'] })
  }
  return {
    clearIds: async (ids) => {
      if (!ids.length) return
      drop(ids)
      try { await api.post('/notifications/inbox/action', { action: 'read', ids }) } catch { /* sonraki yoklama düzeltir */ }
      refresh()
    },
    clearAll: async () => {
      qc.setQueryData(['notifications-panel'], (old) => (old ? { ...old, items: [], unread_count: 0 } : old))
      try { await api.post('/notifications/read-all') } catch { /* yut */ }
      refresh()
    },
  }
}

function useOpener() {
  const navigate = useNavigate()
  const setActiveCompany = useAppStore((s) => s.setActiveCompany)
  const openChatChannel = useChatDock((s) => s.openChannel)
  return (n) => openNotification(n, navigate, setActiveCompany, openChatChannel)
}

// ── kart ───────────────────────────────────────────────────────────────────────
function AppIcon({ app, size = 'w-9 h-9' }) {
  const Icon = app.icon
  return <span className={`${size} shrink-0 rounded-[10px] ${app.color} text-white flex items-center justify-center shadow-sm`}><Icon size={17} strokeWidth={2.2} /></span>
}

function Card({ n, app, extra, onOpen, onClear, fresh, compact }) {
  const badge = companyBadge(n.company)
  return (
    <div className={`group relative rounded-2xl bg-white/95 border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,.06),0_6px_16px_-6px_rgba(0,0,0,.12)]
                     hover:bg-white transition ${fresh ? 'ring-2 ring-primary-300' : ''}`}>
      {onClear && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onClear() }} title="Temizle"
                className="absolute -left-1.5 -top-1.5 z-10 w-5 h-5 rounded-full bg-white border border-gray-200 shadow text-gray-500 hover:text-gray-900
                           flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition">
          <X size={11} strokeWidth={2.6} />
        </button>
      )}
      <button type="button" onClick={onOpen} className="w-full text-left flex gap-3 px-3 py-2.5">
        <AppIcon app={app} />
        <div className="min-w-0 flex-1">
          {n.important && <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 leading-4">Önemli</div>}
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-semibold text-gray-900 leading-5 truncate flex-1">{n.title || app.label}</span>
            <span className="text-[11px] text-gray-400 shrink-0 tabular-nums">{agoText(n.created_at)}</span>
          </div>
          {n.body && <p className={`text-[12.5px] text-gray-600 leading-[1.35] ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>{n.body}</p>}
          {(badge || extra) && (
            <div className="flex items-center gap-1.5 mt-1">
              {badge && <span className={`text-[10px] px-1.5 py-px rounded border font-medium ${badge.cls}`}>{badge.label}</span>}
              {extra && <span className="text-[11px] text-gray-500">{extra}</span>}
            </div>
          )}
        </div>
      </button>
    </div>
  )
}

/** Uygulama grubu: kapalıyken yığın (en yeni kart + arkada iki katman), açıkken tüm kartlar alt alta. */
function AppStack({ app, items, expanded, onToggle, onOpen, onClearIds, freshIds }) {
  const more = items.length - 1
  if (!expanded || items.length === 1) {
    const top = items[0]
    return (
      <div className={`relative ${more > 0 ? 'pb-2.5' : ''}`}>
        {more > 1 && <div className="absolute left-4 right-4 bottom-0 h-6 rounded-2xl bg-white/50 border border-black/[0.04]" />}
        {more > 0 && <div className="absolute left-2 right-2 bottom-1.5 h-6 rounded-2xl bg-white/75 border border-black/[0.05] shadow-sm" />}
        <div className="relative">
          <Card n={top} app={app} fresh={freshIds.has(String(top.id))}
                extra={more > 0 ? `+${more} bildirim daha` : null}
                onOpen={() => (more > 0 ? onToggle() : onOpen(top))}
                onClear={() => onClearIds(items.map((x) => x.id))} />
        </div>
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-center gap-2 px-1 mb-1.5">
        <span className="text-[13px] font-semibold text-gray-800 flex-1 truncate">{app.label}</span>
        <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/80 border border-black/[0.06] text-[11px] font-medium text-gray-700 hover:bg-white">
          <ChevronUp size={12} /> Daha az göster
        </button>
        <button type="button" onClick={() => onClearIds(items.map((x) => x.id))} title="Grubu temizle"
                className="w-5 h-5 rounded-full bg-white/80 border border-black/[0.06] text-gray-500 hover:text-gray-900 flex items-center justify-center"><X size={11} strokeWidth={2.6} /></button>
      </div>
      <div className="space-y-2">
        {items.map((n) => (
          <Card key={n.id} n={n} app={app} compact fresh={freshIds.has(String(n.id))} onOpen={() => onOpen(n)} onClear={() => onClearIds([n.id])} />
        ))}
      </div>
    </div>
  )
}

function groupByApp(items) {
  const map = new Map()
  for (const n of items) {
    const app = appOf(n.type)
    if (!map.has(app.key)) map.set(app.key, { app, items: [] })
    map.get(app.key).items.push(n)
  }
  return [...map.values()]   // sunucu en yeniden eskiye sıralı → grup sırası da en yeni bildirime göre
}

// ── panel ──────────────────────────────────────────────────────────────────────
function PanelBody({ onClose }) {
  const navigate = useNavigate()
  const { data, isLoading } = useNotifFeed()
  const { clearIds, clearAll } = useClear()
  const open = useOpener()
  const [only, setOnly] = useState('all')
  const [expanded, setExpanded] = useState({})
  const freshIds = useFreshIds(data?.items)
  const all = data?.items || []
  const items = only === 'important' ? all.filter((n) => n.important) : all
  const groups = useMemo(() => groupByApp(items), [items])
  const nImp = all.filter((n) => n.important).length

  const openOne = (n) => { clearIds([n.id]); open(n) }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 pt-3.5 pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-gray-900 flex-1">Bildirimler</h2>
          <button type="button" onClick={() => navigate('/notifications')} title="Bildirimler sayfası (tüm geçmiş)" className="p-1.5 rounded-lg text-gray-500 hover:bg-black/5 hover:text-gray-900"><ExternalLink size={15} /></button>
          <button type="button" onClick={onClose} title="Paneli kapat" className="p-1.5 rounded-lg text-gray-500 hover:bg-black/5 hover:text-gray-900"><X size={16} /></button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="inline-flex p-0.5 rounded-lg bg-black/[0.06] text-[12px]">
            {[['all', `Tümü${all.length ? ` ${all.length}` : ''}`], ['important', `Önemli${nImp ? ` ${nImp}` : ''}`]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setOnly(k)}
                      className={`px-2.5 py-1 rounded-md font-medium ${only === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>{l}</button>
            ))}
          </div>
          {all.length > 0 && (
            <button type="button" onClick={clearAll} className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-black/[0.06] text-[11px] font-medium text-gray-700 hover:bg-white">
              <CheckCheck size={12} /> Tümünü temizle
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4 space-y-3">
        {isLoading && [...Array(4)].map((_, i) => <div key={i} className="h-[68px] rounded-2xl bg-white/70 animate-pulse" />)}
        {!isLoading && groups.length === 0 && (
          <div className="flex flex-col items-center text-center pt-16 px-6 text-gray-500">
            <BellOff size={28} className="text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700">{only === 'important' ? 'Önemli bildirim yok' : 'Yeni bildirim yok'}</p>
            <p className="text-xs mt-1">Okunan ve temizlenen bildirimler <button type="button" onClick={() => navigate('/notifications')} className="underline">Bildirimler</button> sayfasında durur.</p>
          </div>
        )}
        {groups.map((g) => (
          <AppStack key={g.app.key} app={g.app} items={g.items} expanded={!!expanded[g.app.key]} freshIds={freshIds}
                    onToggle={() => setExpanded((e) => ({ ...e, [g.app.key]: !e[g.app.key] }))}
                    onOpen={openOne} onClearIds={clearIds} />
        ))}
      </div>
    </div>
  )
}

/** Panele yeni düşen kartları birkaç saniye vurgular. */
function useFreshIds(items) {
  const seen = useRef(null)
  const [fresh, setFresh] = useState(() => new Set())
  useEffect(() => {
    const ids = (items || []).map((n) => String(n.id))
    if (seen.current === null) { seen.current = new Set(ids); return undefined }
    const nw = ids.filter((id) => !seen.current.has(id))
    ids.forEach((id) => seen.current.add(id))
    if (!nw.length) return undefined
    setFresh(new Set(nw))
    const t = setTimeout(() => setFresh(new Set()), 4000)
    return () => clearTimeout(t)
  }, [items])
  return fresh
}

/** Yerleşik sağ panel (geniş ekran) + dar ekranda üste açılan çekmece. Layout'a konur. */
export default function NotificationPanel() {
  const wide = useWide()
  const open = useNotifPanel((s) => s.open)
  const overlay = useNotifPanel((s) => s.overlay)
  const close = useNotifPanel((s) => s.close)
  if (wide) {
    if (!open) return null
    return (
      <aside className="flex-none w-[360px] border-l border-gray-200 bg-gray-100/80 backdrop-blur-xl animate-[dockin_.16s_ease-out]" aria-label="Bildirimler">
        <PanelBody onClose={close} />
      </aside>
    )
  }
  if (!overlay) return null
  return (
    <div className="fixed inset-0 z-[45]">
      <div className="absolute inset-0 bg-gray-900/20" onClick={close} />
      <aside className="absolute right-0 top-0 bottom-0 w-[min(380px,100vw)] bg-gray-100/95 backdrop-blur-xl shadow-2xl animate-[ncin_.18s_ease-out]" aria-label="Bildirimler">
        <PanelBody onClose={close} />
      </aside>
    </div>
  )
}

// ── gelen bildirim banner'ı ────────────────────────────────────────────────────
const POPPED_KEY = 'vertex_popped_notifications'
const readPopped = () => { try { return new Set(JSON.parse(localStorage.getItem(POPPED_KEY) || '[]')) } catch { return new Set() } }
const writePopped = (s) => { try { localStorage.setItem(POPPED_KEY, JSON.stringify([...s].slice(-300))) } catch { /* kota */ } }
const BANNER_MS = 8000

function Banner({ n, onClose, onOpen }) {
  const app = appOf(n.type)
  const [hover, setHover] = useState(false)
  useEffect(() => {
    if (n.important || hover) return undefined       // önemli bildirim kapatılana kadar kalır (macOS "uyarı")
    const t = setTimeout(onClose, BANNER_MS)
    return () => clearTimeout(t)
  }, [n.important, hover, onClose])
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
         className="w-[360px] max-w-[calc(100vw-24px)] animate-[ncin_.22s_ease-out] rounded-2xl backdrop-blur-xl bg-white/90 shadow-[0_10px_30px_-8px_rgba(0,0,0,.25)] border border-black/[0.06]">
      <div className="group relative">
        <button type="button" onClick={onClose} title="Kapat"
                className="absolute -left-1.5 -top-1.5 z-10 w-5 h-5 rounded-full bg-white border border-gray-200 shadow text-gray-500 hover:text-gray-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
          <X size={11} strokeWidth={2.6} />
        </button>
        <button type="button" onClick={onOpen} className="w-full text-left flex gap-3 px-3 py-2.5">
          <AppIcon app={app} />
          <div className="min-w-0 flex-1">
            {n.important && <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 leading-4">Önemli</div>}
            <div className="flex items-baseline gap-2">
              <span className="text-[13px] font-semibold text-gray-900 leading-5 truncate flex-1">{n.title || app.label}</span>
              <span className="text-[11px] text-gray-400 shrink-0">{agoText(n.created_at)}</span>
            </div>
            {n.body && <p className="text-[12.5px] text-gray-600 leading-[1.35] line-clamp-3">{n.body}</p>}
            <div className="text-[11px] text-gray-400 mt-0.5">{app.label}{companyBadge(n.company) ? ` · ${companyBadge(n.company).label}` : ''}</div>
          </div>
        </button>
      </div>
    </div>
  )
}

/** Yeni bildirimi yakalar: panel ekrandaysa kart panele düşer (vurgulu), değilse sağ üstte banner çıkar. */
export function NotificationBanners() {
  const { data } = useNotifFeed()
  const panelVisible = useNotifPanelVisible()
  const { clearIds } = useClear()
  const open = useOpener()
  const [banners, setBanners] = useState([])
  const primed = useRef(false)
  useEffect(() => {
    const items = data?.items || []
    if (!data) return
    const seen = readPopped()
    if (!primed.current) {                         // ilk yükleme: mevcutlar sessizce "görüldü" (20 banner patlamasın)
      primed.current = true
      items.forEach((n) => n?.id != null && seen.add(String(n.id)))
      writePopped(seen)
      return
    }
    const fresh = items.filter((n) => n?.id != null && !seen.has(String(n.id)))
    if (!fresh.length) return
    fresh.forEach((n) => seen.add(String(n.id)))
    writePopped(seen)
    if (panelVisible) return
    // Sohbet açıkken sohbet mesajı bildirimi banner olmaz — mesaj zaten akışta (eski kural korunur).
    let inChat = false
    try { inChat = !!useChatDock.getState().open || String(window.location?.pathname || '').startsWith('/chat') } catch { /* yut */ }
    const show = (inChat ? fresh.filter((n) => !String(n.type || '').startsWith('chat.')) : fresh).slice(0, 3)
    if (show.length) setBanners((b) => [...show, ...b].slice(0, 3))
  }, [data, panelVisible])
  useEffect(() => { if (panelVisible) setBanners([]) }, [panelVisible])
  if (!banners.length) return null
  return (
    <div className="fixed right-3 top-16 z-[90] flex flex-col gap-2">
      {banners.map((n) => (
        <Banner key={n.id} n={n}
                onClose={() => setBanners((b) => b.filter((x) => x.id !== n.id))}
                onOpen={() => { setBanners((b) => b.filter((x) => x.id !== n.id)); clearIds([n.id]); open(n) }} />
      ))}
    </div>
  )
}

/** Header zili: rozet = okunmamış sayısı; tık = paneli aç/kapat. */
export function NotificationBellButton() {
  const { data } = useNotifFeed()
  const toggle = useNotifPanel((s) => s.toggle)
  const visible = useNotifPanelVisible()
  const unread = data?.unread_count ?? 0
  return (
    <button type="button" onClick={toggle} title={visible ? 'Bildirim panelini kapat' : 'Bildirim panelini aç'}
            className={`relative p-2 rounded-lg transition-colors ${visible ? 'bg-surface-100' : 'hover:bg-surface-100'}`}>
      <Bell size={20} className={visible ? 'text-gray-900' : 'text-text-secondary'} />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  )
}

